import { FieldLabelText } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { IGridProps, IHorizontalGridProps } from 'Components/Layout/Grid/types';
import { hasValue } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import { joinPaths } from 'Lib/Object/Path/shared';
import deepEquals from 'Lib/Object/deepEquals';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RecordGridField, RecordGridFieldSet, RecordProps, isIndexVariable } from '.';
import { IWithWorksheetInjectedProps } from '..';
import { Grid, HorizontalGrid } from '../../../Layout';
import { ArrayModifierPayLoad } from '../WorksheetEditorInjectedPropsProvider/types';
import { generateFields } from './generateFields';
import { getRecordHeader, isCreatePrevented, isRemovePrevented } from './helpers';

type WorksheetRecordGridProps = IWithWorksheetInjectedProps &
  Pick<IGridProps, 'tabDirection' | 'onReceivedData' | 'rowHeaderWidth'> &
  Pick<IHorizontalGridProps, 'scrollLinkId'> & {
    /**
     * The set of fields to be displayed for each record
     **/
    fieldSet: RecordGridFieldSet;
    /**
     * Orientation of the grid. Note "horizontal" means each column is a record, "vertical" means each row is a record.
     **/
    orientation?: 'horizontal' | 'vertical';
    /**
     * Properties that define how records are displayed and managed by this grid
     */
    recordProps?: RecordProps;
  };

/**
 * Constructs a Grid component for a set of records on a worksheet using the
 * provided fieldSet
 *
 * Essentially a wrapper for {@link Grid} and {@link HorizontalGrid}
 * to provide common functionality for manipulating records that we do not want to replicate on each worksheet.
 *
 * Work in progress. Will require additional work to extend the functionality it covers.
 */
let WorksheetRecordGrid: React.FC<WorksheetRecordGridProps> = ({
  fieldSet,
  name,
  prefixName,
  createWorksheetSelector,
  addArrayEntry,
  removeArrayEntry,
  orientation = 'horizontal',
  recordProps = {},
  rowHeaderWidth = 'auto',
  ...gridProps
}) => {
  const recordCount = useSelector(
    pipe(
      createWorksheetSelector!(name),
      (records) => records?.length ?? 0,
      (c) => (c <= 0 && recordProps.enforceAtLeastOneRecord ? 1 : c)
    ),
    deepEquals
  );

  /**
   * Allows records to be managed in "groups" (e.g. MDD trial pairs)
   */
  const recordGroupingSize = recordProps?.recordGroupingSize ?? 1;

  const createRecords = useCallback(
    (startIndex: number) => {
      [...Array(recordGroupingSize).keys()].forEach((k) =>
        addArrayEntry!({
          name: prefixName!(name!),
          index: k + startIndex,
          data: undefined,
          sync: k === recordGroupingSize - 1,
        })
      );
    },
    [addArrayEntry, name, prefixName, recordGroupingSize]
  );

  const removeRecords = useCallback(
    (endIndex: number) => {
      [...Array(recordGroupingSize).keys()].forEach((k) =>
        removeArrayEntry!({
          name: prefixName!(name!),
          index: endIndex - k,
          data: undefined,
          sync: k === recordGroupingSize - 1,
        })
      );
    },
    [removeArrayEntry, name, prefixName, recordGroupingSize]
  );

  // TODO: This selector triggers re-rendering more often than should be required. To be investigated.
  const fields: Array<RecordGridField> = useSelector(
    pipe(createWorksheetSelector!(name), (records) => [
      ...generateFields(
        records,
        fieldSet,
        name!,
        (a: ArrayModifierPayLoad) => addArrayEntry!({ ...a, name: prefixName!(a.name) }),
        (a: ArrayModifierPayLoad) => removeArrayEntry!({ ...a, name: prefixName!(a.name) })
      ),
    ]),
    deepEquals
  );

  const fieldHeaders = fields.map((f, i) =>
    isIndexVariable(f.label) && f.fieldSubRecordIndex !== undefined ? (
      f.label(f.fieldSubRecordIndex)
    ) : hasValue(f.label) ? (
      f.label
    ) : (
      <FieldLabelText name={f.name} />
    )
  );
  const recordHeaders = recordProps?.heading
    ? Array(recordCount)
        .fill(undefined)
        .map((_, i) => getRecordHeader(recordProps?.heading, i))
    : undefined;

  if (orientation === 'horizontal')
    return (
      <NamePrefixModifier appendNamePrefix={name}>
        <HorizontalGrid
          rowHeaders={fieldHeaders as any}
          colHeaders={recordHeaders}
          cornerHeader={recordProps?.headingLabel}
          data={Array(recordCount)
            .fill(undefined)
            .map((_, i) =>
              fields.map((f) =>
                React.cloneElement(isIndexVariable(f.component) ? f.component(i) : f.component, {
                  name: joinPaths(i.toString(), f.name),
                  additionalMessagePaths: f.additionalMessagePaths?.map((p) => joinPaths(i.toString(), p)),
                })
              )
            )}
          columns={Array(recordCount)
            .fill(undefined)
            .map((_, i) => ({
              preventRemoveCol: isRemovePrevented(i, recordCount, recordProps),
              dim: recordProps?.dim?.(i),
            }))}
          onCreateCol={isCreatePrevented(recordCount, recordProps) ? undefined : () => createRecords(recordCount ?? 0)}
          onRemoveCol={(i) => removeRecords(i)}
          rowHeaderWidth={rowHeaderWidth}
          colWidths={recordProps?.width}
          removeConfirm={recordProps?.removeConfirm}
          {...gridProps}
        />
      </NamePrefixModifier>
    );

  return (
    <NamePrefixModifier appendNamePrefix={name}>
      <Grid
        colHeaders={fieldHeaders as any}
        rowHeaders={recordHeaders}
        cornerHeader={recordProps?.headingLabel}
        data={Array(recordCount)
          .fill(undefined)
          .map((_, i) =>
            fields.map((f) =>
              React.cloneElement(isIndexVariable(f.component) ? f.component(i) : f.component, {
                name: joinPaths(i.toString(), f.name),
                additionalMessagePaths: f.additionalMessagePaths?.map((p) => joinPaths(i.toString(), p)),
              })
            )
          )}
        rows={Array(recordCount)
          .fill(undefined)
          .map((_, i) => ({
            preventRemoveRow: isRemovePrevented(i, recordCount, recordProps),
            dim: recordProps?.dim?.(i),
          }))}
        onCreateRow={isCreatePrevented(recordCount, recordProps) ? undefined : () => createRecords(recordCount ?? 0)}
        onRemoveRow={(i) => removeRecords(i)}
        removeConfirm={recordProps?.removeConfirm}
        rowHeaderWidth={rowHeaderWidth}
        colWidths={fields.map((f) => f.width)}
        {...gridProps}
      />
    </NamePrefixModifier>
  );
};

WorksheetRecordGrid = withInjectedProps(WorksheetRecordGrid);

export { WorksheetRecordGrid };
export default WorksheetRecordGrid;
